import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createFuncionario } from '../../services/funcionarioService';
import ConfirmModal from '../../components/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave } from '@fortawesome/free-solid-svg-icons';

// Função para converter campo boolean que pode vir como buffer do MySQL
const convertBooleanField = (value) => {
  if (value && typeof value === 'object' && value.type === 'Buffer') {
    return value.data[0] === 1;
  } else if (typeof value === 'number') {
    return value === 1;
  } else if (typeof value === 'boolean') {
    return value;
  }
  return false;
};

const initialState = {
  nome: '',
  cpf: '',
  dataNascimento: '',
  email: '',
  telefoneCelular: '',
  telefoneResidencial: '',
  telefoneComercial: '',
  escolaridade: '',
  estadoCivil: '',
  status: '',
  identidade: '',
  orgaoExpedidor: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  pais: '',
  estrangeira: false,
  docEstrangeiro: ''
};

const FuncionarioCreate = () => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({}); // Estado para erros por campo
  const navigate = useNavigate();

  // Função para obter classes CSS do campo (com erro ou normal)
  const getFieldClass = (fieldName, baseClass = "w-full border rounded px-3 py-2") => {
    return fieldErrors[fieldName] 
      ? `${baseClass} border-red-500 focus:ring-red-400 bg-red-50`
      : `${baseClass} focus:outline-none focus:ring-2 focus:ring-green-400`;
  };

  // Limpar mensagens de erro após 8 segundos (apenas erro geral, não fieldErrors)
  useEffect(() => {
    if (error && Object.keys(fieldErrors).length === 0) {
      const timer = setTimeout(() => {
        setError('');
      }, 8000);
      return () => clearTimeout(timer);
    }
    // Se houver fieldErrors, NÃO limpar automaticamente - usuário precisa corrigir
  }, [error, fieldErrors]);

  // Auto limpar mensagens de sucesso após 3 segundos
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Scroll para o topo quando houver mensagens de feedback
  useEffect(() => {
    if (error || success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, success]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
    setError('');
    // Limpar erro específico do campo quando usuário começar a digitar
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: null });
    }
  };

  // Funções de máscara em tempo real
  const maskCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1-$2')
      .slice(0, 14);
  };

  const maskCEP = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2').slice(0, 9);
  };

  const maskPhone = (value, isCellPhone = false) => {
    let cleaned = value.replace(/\D/g, '');
    
    if (isCellPhone) {
      // Celular: (XX)XXXXX-XXXX - máximo 11 dígitos
      cleaned = cleaned.slice(0, 11); // Limita a 11 dígitos
      
      if (cleaned.length <= 2) {
        return cleaned.replace(/(\d{1,2})/, '($1');
      } else if (cleaned.length <= 7) {
        return cleaned.replace(/(\d{2})(\d+)/, '($1)$2');
      } else {
        return cleaned.replace(/(\d{2})(\d{5})(\d+)/, '($1)$2-$3');
      }
    } else {
      // Telefone fixo: pode ter 8 ou 9 dígitos após o DDD
      cleaned = cleaned.slice(0, 11); // Máximo 11 dígitos (DDD + 9 dígitos)
      
      if (cleaned.length <= 2) {
        return cleaned.replace(/(\d{1,2})/, '($1');
      } else if (cleaned.length <= 6) {
        return cleaned.replace(/(\d{2})(\d+)/, '($1)$2');
      } else if (cleaned.length <= 10) {
        // 8 dígitos: (XX)XXXX-XXXX
        return cleaned.replace(/(\d{2})(\d{4})(\d+)/, '($1)$2-$3');
      } else {
        // 9 dígitos: (XX)XXXXX-XXXX
        return cleaned.replace(/(\d{2})(\d{5})(\d+)/, '($1)$2-$3');
      }
    }
  };

  const handleChangeMasked = (e) => {
    const { name, value } = e.target;
    let maskedValue = value;

    switch (name) {
      case 'cpf':
        maskedValue = maskCPF(value);
        break;
      case 'cep':
        maskedValue = maskCEP(value);
        break;
      case 'telefoneCelular':
        maskedValue = maskPhone(value, true);
        break;
      case 'telefoneResidencial':
      case 'telefoneComercial':
        maskedValue = maskPhone(value, false);
        break;
      default:
        break;
    }

    setForm({ ...form, [name]: maskedValue });
    setError('');
    // Limpar erro específico do campo quando usuário começar a digitar
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: null });
    }
  };

  /**
   * Campos marcados com * no formulário. O backend segue como autoridade;
   * isto apenas evita a confirmação inútil de um cadastro que será recusado.
   * @returns {Record<string,string>}
   */
  const validarCampos = () => {
    const erros = {};
    const obrigatorio = (campo, rotulo) => {
      if (!(form[campo] || '').toString().trim()) erros[campo] = `${rotulo} é obrigatório.`;
    };

    obrigatorio('nome', 'Nome');
    obrigatorio('dataNascimento', 'Data de nascimento');
    obrigatorio('status', 'Status');

    if (form.estrangeira) {
      obrigatorio('docEstrangeiro', 'Documento estrangeiro');
    } else if (!(form.cpf || '').trim()) {
      erros.cpf = 'CPF é obrigatório.';
    } else if (!/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(form.cpf.trim())) {
      erros.cpf = 'CPF incompleto — informe os 11 dígitos.';
    }

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) erros.email = 'E-mail inválido.';

    return erros;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const erros = validarCampos();
    if (Object.keys(erros).length > 0) {
      setFieldErrors(erros);
      setError('Por favor, corrija os campos destacados em vermelho.');

      const primeiro = document.querySelector(`[name="${Object.keys(erros)[0]}"]`);
      if (primeiro) {
        primeiro.scrollIntoView({ behavior: 'smooth', block: 'center' });
        primeiro.focus();
      }
      return;
    }

    setFieldErrors({});
    setError('');
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      // Preparar dados condicionalmente baseado no campo estrangeira
      const dadosParaEnvio = { ...form };
      
      if (form.estrangeira) {
        // Se é estrangeiro, garantir que CPF seja null
        dadosParaEnvio.cpf = null;
      } else {
        // Se não é estrangeiro, garantir que documento estrangeiro seja null
        dadosParaEnvio.docEstrangeiro = null;
      }
      
      await createFuncionario(dadosParaEnvio);
      setSuccess(true);
      setSuccessMessage(`Funcionário "${form.nome}" foi cadastrado com sucesso!`);
      setTimeout(() => navigate('/funcionarios'), 2000);
    } catch (err) {
      console.error('Erro ao cadastrar funcionário:', err);
      
      // Processar erros de validação específicos do backend.
      // O middleware devolve a lista em `validationErrors`; o código lia
      // `errors`, então nenhum erro de campo aparecia e o usuário recebia
      // "Tente novamente" — que sugere falha temporária quando na verdade
      // faltava preencher um campo.
      const dados = err.response?.data;
      const listaDeErros = dados?.validationErrors || dados?.errors;

      if (err.response?.status === 400 && Array.isArray(listaDeErros) && listaDeErros.length) {
        const errors = {};
        listaDeErros.forEach(error => {
          const campo = error.path || error.param;
          if (campo && !errors[campo]) errors[campo] = error.msg;
        });
        setFieldErrors(errors);
        setError('Por favor, corrija os campos destacados em vermelho.');
      } else if (err.response?.status === 400 && dados?.message) {
        setError(dados.message);
      } else {
        const errorMessage = err.response?.status === 403
          ? 'Você não tem permissão para cadastrar funcionários.'
          : err.response?.status === 409
          ? 'Já existe um funcionário cadastrado com este CPF ou documento.'
          : 'Não foi possível cadastrar o funcionário. Verifique sua conexão e tente novamente.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Cadastrar Funcionário</h2>
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-green-800 font-medium">{successMessage || 'Funcionário cadastrado com sucesso!'}</p>
                <p className="text-green-600 text-sm mt-1">Redirecionando para a lista de funcionários...</p>
              </div>
            </div>
          </div>
        )}      
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Dados Pessoais */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Dados Pessoais</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome *</label>
              <input type="text" name="nome" value={form.nome} onChange={handleChange} maxLength={100} className={getFieldClass('nome')} placeholder="Nome completo" />
              {fieldErrors.nome && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.nome}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Data de Nascimento *</label>
              <input type="date" name="dataNascimento" value={form.dataNascimento} onChange={handleChange} className={getFieldClass('dataNascimento')} />
              {fieldErrors.dataNascimento && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.dataNascimento}</p>
              )}
            </div>
            <div className="flex items-center mt-6">
              <input type="checkbox" name="estrangeira" checked={form.estrangeira} onChange={handleChange} className="mr-2" />
              <label className="text-sm">Estrangeiro</label>
            </div>
            {!form.estrangeira ? (
              <div>
                <label className="block text-sm font-medium mb-1">CPF *</label>
                <input type="text" name="cpf" value={form.cpf} onChange={handleChangeMasked} maxLength={14} className={getFieldClass('cpf')} placeholder="000.000.000-00" />
                {fieldErrors.cpf && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.cpf}</p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1">Documento Estrangeiro *</label>
                <input type="text" name="docEstrangeiro" value={form.docEstrangeiro} onChange={handleChange} maxLength={50} className={getFieldClass('docEstrangeiro')} placeholder="Documento de identificação" />
                {fieldErrors.docEstrangeiro && (
                  <p className="text-red-600 text-sm mt-1">{fieldErrors.docEstrangeiro}</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Identidade</label>
              <input type="text" name="identidade" value={form.identidade} onChange={handleChange} maxLength={20} className="w-full border rounded px-3 py-2" placeholder="Identidade" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Órgão Expedidor</label>
              <input type="text" name="orgaoExpedidor" value={form.orgaoExpedidor} onChange={handleChange} maxLength={30} className="w-full border rounded px-3 py-2" placeholder="Órgão Expedidor" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} maxLength={60} className="w-full border rounded px-3 py-2" placeholder="Email" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Celular</label>
              <input type="text" name="telefoneCelular" value={form.telefoneCelular} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)00000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Residencial</label>
              <input type="text" name="telefoneResidencial" value={form.telefoneResidencial} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Telefone Comercial</label>
              <input type="text" name="telefoneComercial" value={form.telefoneComercial} onChange={handleChangeMasked} maxLength={15} className="w-full border rounded px-3 py-2" placeholder="(00)0000-0000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Escolaridade</label>
              <select name="escolaridade" value={form.escolaridade} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Selecione a Escolaridade</option>
                <option value="Ensino Fundamental Incompleto">Ensino Fundamental Incompleto</option>
                <option value="Ensino Fundamental Completo">Ensino Fundamental Completo</option>
                <option value="Ensino Médio Incompleto">Ensino Médio Incompleto</option>
                <option value="Ensino Médio Completo">Ensino Médio Completo</option>
                <option value="Ensino Superior Incompleto">Ensino Superior Incompleto</option>
                <option value="Ensino Superior Completo">Ensino Superior Completo</option>
                <option value="Pós-graduação">Pós-graduação</option>
                <option value="Mestrado">Mestrado</option>
                <option value="Doutorado">Doutorado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado Civil</label>
              <select name="estadoCivil" value={form.estadoCivil} onChange={handleChange} className="w-full border rounded px-3 py-2">
                <option value="">Selecione o Estado Civil</option>
                <option value="Solteiro(a)">Solteiro(a)</option>
                <option value="Casado(a)">Casado(a)</option>
                <option value="Divorciado(a)">Divorciado(a)</option>
                <option value="Viúvo(a)">Viúvo(a)</option>
                <option value="Separado(a)">Separado(a)</option>
                <option value="União Estável">União Estável</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status *</label>
              <select name="status" value={form.status} onChange={handleChange} className={getFieldClass('status')}>
                <option value="">Selecione o Status</option>
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
              </select>
              {fieldErrors.status && (
                <p className="text-red-600 text-sm mt-1">{fieldErrors.status}</p>
              )}
            </div>
          </div>
        </div>
        {/* Endereço */}
        <div>
          <h3 className="font-bold text-lg mb-2 text-green-700">Endereço</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">CEP</label>
              <input type="text" name="cep" value={form.cep} onChange={handleChangeMasked} maxLength={10} className="w-full border rounded px-3 py-2" placeholder="00000-000" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Logradouro</label>
              <input type="text" name="logradouro" value={form.logradouro} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Logradouro" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Número</label>
              <input type="text" name="numero" value={form.numero} onChange={handleChange} maxLength={30} className="w-full border rounded px-3 py-2" placeholder="Número" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Complemento</label>
              <input type="text" name="complemento" value={form.complemento} onChange={handleChange} maxLength={255} className="w-full border rounded px-3 py-2" placeholder="Complemento" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bairro</label>
              <input type="text" name="bairro" value={form.bairro} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="Bairro" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cidade</label>
              <input type="text" name="cidade" value={form.cidade} onChange={handleChange} maxLength={100} className="w-full border rounded px-3 py-2" placeholder="Cidade" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Estado</label>
              <input type="text" name="estado" value={form.estado} onChange={handleChange} maxLength={2} className="w-full border rounded px-3 py-2" placeholder="UF" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">País</label>
              <input type="text" name="pais" value={form.pais} onChange={handleChange} maxLength={50} className="w-full border rounded px-3 py-2" placeholder="País" />
            </div>
          </div>
        </div>
        
        {/* Mensagens de Feedback */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-red-800 font-medium whitespace-pre-line">{error}</p>
              </div>
              <div className="ml-auto">
                <button 
                  onClick={() => setError('')}
                  className="text-red-400 hover:text-red-600"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex gap-4 mt-6 justify-end">
          <button
            type="button"
            className="px-4 py-2 rounded bg-gray-400 text-white font-semibold shadow hover:bg-gray-500 focus:outline-none flex items-center gap-2"
            onClick={() => navigate('/funcionarios')}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button
            type="submit"
            className={`px-4 py-2 rounded font-semibold shadow focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2 ${
              loading 
                ? 'bg-green-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
            disabled={loading}
           aria-label="Salvar Funcionário">
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="hidden sm:inline">Salvando...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span className="hidden sm:inline">Salvar Funcionário</span>
              </>
            )}
          </button>
        </div>
      </form>
      <ConfirmModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        title="Confirmar Cadastro"
        message={`Tem certeza que deseja cadastrar o funcionário "${form?.nome}"?`}
      />
    </div>
  );
};

export default FuncionarioCreate;
